import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Drag & drop ordering for the Learning Center admin pages.
//
// Three modes — selected via `data-learning-center-builder-mode-value`:
//   "chapters" (default)  — reorders chapters under a course
//   "lessons"             — reorders lessons under a chapter
//   "elements"            — reorders elements under a lesson
//
// Each mode posts to a different endpoint. Routes follow the existing
// nested resource structure under `juniors/:id/[parcours/:id]/...`.
export default class extends Controller {
  static targets = ["chapterPosition"];
  static values = {
    juniorId:  String,
    parcourId: String,
    courseId:  String,
    chapterId: String,
    lessonId:  String,
    mode:      { type: String, default: "chapters" }
  };

  connect() {
    const handle = this.modeValue === "elements"
      ? ".lc-element__handle, .lc-chapter__handle, .lc-lesson__handle"
      : ".lc-chapter__handle, .lc-lesson__handle";

    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle,
      ghostClass: "lc-sortable-ghost",
      onEnd: (evt) => this._onEnd(evt)
    });
  }

  disconnect() {
    this.sortable?.destroy();
  }

  async _onEnd(evt) {
    switch (this.modeValue) {
      case "chapters":
        await this._reorderChapter(evt);
        break;
      case "lessons":
        await this._reorderLessons();
        break;
      case "elements":
        await this._reorderElement(evt);
        break;
      default:
        break;
    }
  }

  async _reorderChapter(evt) {
    const id = evt.item.dataset.chapterId;
    const newPosition = evt.newIndex + 1;
    const path = this.parcourIdValue && this.parcourIdValue !== ""
      ? `/juniors/${this.juniorIdValue}/parcours/${this.parcourIdValue}/courses/${this.courseIdValue}/chapters/${id}/move`
      : `/juniors/${this.juniorIdValue}/courses/${this.courseIdValue}/chapters/${id}/move`;

    await this._fetch(path, "PATCH", { position: newPosition });
    this._renumberChapters();
  }

  async _reorderLessons() {
    const order = Array.from(this.element.querySelectorAll("[data-lesson-id]")).map((el, idx) => ({
      id: el.dataset.lessonId,
      position: idx + 1
    }));
    const path = this.parcourIdValue && this.parcourIdValue !== ""
      ? `/juniors/${this.juniorIdValue}/parcours/${this.parcourIdValue}/courses/${this.courseIdValue}/chapters/${this.chapterIdValue}/update_lesson_positions`
      : `/juniors/${this.juniorIdValue}/courses/${this.courseIdValue}/chapters/${this.chapterIdValue}/update_lesson_positions`;
    await this._fetch(path, "PATCH", { order });
    this._renumberLessons();
  }

  async _reorderElement(evt) {
    const id = evt.item.dataset.elementId;
    const newPosition = evt.newIndex + 1;
    const path = this.parcourIdValue && this.parcourIdValue !== ""
      ? `/juniors/${this.juniorIdValue}/parcours/${this.parcourIdValue}/courses/${this.courseIdValue}/chapters/${this.chapterIdValue}/lessons/${this.lessonIdValue}/lesson_elements/${id}/update_position`
      : `/juniors/${this.juniorIdValue}/courses/${this.courseIdValue}/chapters/${this.chapterIdValue}/lessons/${this.lessonIdValue}/lesson_elements/${id}/update_position`;
    await this._fetch(path, "PATCH", { position: newPosition });
  }

  _renumberChapters() {
    this.chapterPositionTargets.forEach((el, idx) => {
      el.textContent = idx + 1;
    });
  }

  _renumberLessons() {
    this.element.querySelectorAll(".lc-lesson__index").forEach((el, idx) => {
      el.textContent = idx + 1;
    });
  }

  async _fetch(path, method, body) {
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      await fetch(path, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf || "",
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify(body)
      });
    } catch (err) {
      // Silent fail — re-trigger persistence on next user interaction.
      // eslint-disable-next-line no-console
      console.error("Learning Center reorder failed", err);
    }
  }
}
